<template>
  <div class="mortgage-progress-show-component" v-bind:class="{loaded}">
    <a-progress v-bind:percent="progress" style="margin-bottom: 15px;" v-if="loaded"/>
    <Skeleton height="8px" style="display: block; margin-bottom: 15px;" v-else v-bind:loading="true"></Skeleton>

    <h4>Te ontvangen stukken</h4>

    <div class="mortgage-progress-list" v-if="loaded && hasMortgageProgressToBeReceived">
      <MortgageProgressShowItemComponent v-for="mortgageProgress in mortgageProgressToBeReceived"
                                         v-bind:key="mortgageProgress.id"
                                         v-bind:mortgage-progress="mortgageProgress"
                                         v-on:updated="onMortgageProgressUpdated"
      />
    </div>

    <Skeleton v-else-if="!loaded" v-bind:loading="true" style="height: 50px; display: block; margin-bottom: 15px;"></Skeleton>

    <a-empty v-else-if="!hasMortgageProgressToBeReceived" v-bind:image="simpleImage" class="empty-mortgage-progress"
             v-bind:description="null"/>

    <h4>Te versturen naar maatschappij</h4>

    <div class="mortgage-progress-list" v-if="loaded && hasMortgageProgressToSendToAgency">
      <MortgageProgressShowItemComponent v-for="mortgageProgress in mortgageProgressToSendToAgency"
                                         v-bind:key="mortgageProgress.id"
                                         v-bind:mortgage-progress="mortgageProgress"
                                         v-on:updated="onMortgageProgressUpdated"
      />

      <DeclineMortgageProgressesComponent
          :decline-items="declineSelectionToSendToAgency"
          @declined="clearDeclineSelectionToSendToAgency"
      ></DeclineMortgageProgressesComponent>
    </div>

    <Skeleton v-else-if="!loaded" v-bind:loading="true" style="height: 50px; display: block; margin-bottom: 15px;"></Skeleton>

    <a-empty v-else-if="!hasMortgageProgressToSendToAgency" v-bind:image="simpleImage" class="empty-mortgage-progress"
             v-bind:description="null"/>

    <h4>Goed te keuren door maatschappij</h4>

    <div class="mortgage-progress-list" v-if="loaded && hasMortgageProgressToBeApproved">
      <MortgageProgressShowItemComponent v-for="mortgageProgress in mortgageProgressToBeApproved"
                                         v-bind:key="mortgageProgress.id"
                                         v-bind:mortgage-progress="mortgageProgress"
                                         v-on:updated="onMortgageProgressUpdated"
      />

      <DeclineMortgageProgressesComponent
          :decline-items="declineSelectionToBeApproved"
          @declined="clearDeclineSelectionToBeApproved"
      ></DeclineMortgageProgressesComponent>
    </div>

    <Skeleton v-else-if="!loaded" v-bind:loading="true" style="height: 50px; display: block; margin-bottom: 15px;"></Skeleton>

    <a-empty v-else-if="!hasMortgageProgressToBeApproved" v-bind:image="simpleImage" class="empty-mortgage-progress"
             v-bind:description="null"/>

    <h4>Goedgekeurd</h4>

    <div class="mortgage-progress-list" v-if="loaded && hasMortgageProgressApproved">
      <MortgageProgressShowItemComponent v-for="mortgageProgress in mortgageProgressApproved"
                                         v-bind:key="mortgageProgress.id"
                                         v-bind:mortgage-progress="mortgageProgress"
                                         v-on:updated="onMortgageProgressUpdated"
      />

      <DeclineMortgageProgressesComponent
          :decline-items="declineSelectionApproved"
          @declined="clearDeclineSelectionApproved"
      ></DeclineMortgageProgressesComponent>
    </div>

    <Skeleton v-else-if="!loaded" v-bind:loading="true" style="height: 50px; display: block; margin-bottom: 15px;"></Skeleton>

    <a-empty v-else-if="!hasMortgageProgressApproved" v-bind:image="simpleImage" class="empty-mortgage-progress"
             v-bind:description="null"/>

    <a-button type="primary" v-on:click="onPrintClick">Printen</a-button>
  </div>
</template>

<script>
import {Empty} from 'ant-design-vue'
import mortgageProgressStore from '../store'
import mortgageApi from '~/api/mortgage'
import mortgageProgressApi from '~/api/mortgage_progress'
import documentTypeApi from '~/api/document_type'
import {Skeleton} from 'vue-loading-skeleton'
import MortgageProgressShowItemComponent from './ShowItemComponent'
import DeclineMortgageProgressesComponent from './DeclineMortgageProgressesComponent'
import {mapActions, mapGetters} from "vuex";

export default {
  name: 'MortgageProgressShowComponent',
  components: {
    MortgageProgressShowItemComponent,
    DeclineMortgageProgressesComponent,
    Skeleton,
    Empty
  },
  props: {
    mortgageId: {
      required: false,
      default: null
    },
    mortgageProp: {
      type: Object,
      required: false,
      default: null
    },
  },
  data () {
    return {
      documentTypes: [],
      mortgageProgressGroup: null,
      loaded: false,
      searchQuery: '',
      sortType: 'hdn',
    }
  },
  beforeCreate () {
    this.simpleImage = Empty.PRESENTED_IMAGE_SIMPLE
  },
  mounted () {
    const STORE_KEY = '$_mortgage_progress';
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, mortgageProgressStore);
    }

    if (this.mortgageProp === null && this.mortgageId === null) {
      throw new Error('Either the mortgage object or mortgage id is required.')
    }

    if (this.mortgageProp === null) {
      mortgageApi.find(this.mortgageId).then(response => {
        this.setMortgage(response.data).then(() => {
          this.initMortgageProgress(this.mortgage.id)
        })
      }).finally(() => {
        this.loaded = true
      })
    } else {
      this.setMortgage(this.mortgageProp).then(() => {
        this.initMortgageProgress(this.mortgage.id)
      }).finally(() => {
        this.loaded = true
      })
    }
  },
  computed: {
    ...mapGetters({
      mortgage: "$_mortgage_progress/mortgage",
      mortgageProgresses: "$_mortgage_progress/mortgageProgresses",
      sortedMortgageProgresses: "$_mortgage_progress/sortedMortgageProgresses",

      mortgageProgressToBeReceived: "$_mortgage_progress/mortgageProgressToBeReceived",
      mortgageProgressToSendToAgency: "$_mortgage_progress/mortgageProgressToSendToAgency",
      mortgageProgressToBeApproved: "$_mortgage_progress/mortgageProgressToBeApproved",
      mortgageProgressApproved: "$_mortgage_progress/mortgageProgressApproved",

      hasMortgageProgressToBeReceived: "$_mortgage_progress/hasMortgageProgressToBeReceived",
      hasMortgageProgressToSendToAgency: "$_mortgage_progress/hasMortgageProgressToSendToAgency",
      hasMortgageProgressToBeApproved: "$_mortgage_progress/hasMortgageProgressToBeApproved",
      hasMortgageProgressApproved: "$_mortgage_progress/hasMortgageProgressApproved",

      declineSelectionToSendToAgency: "$_mortgage_progress/declineSelectionToSendToAgency",
      declineSelectionToBeApproved: "$_mortgage_progress/declineSelectionToBeApproved",
      declineSelectionApproved: "$_mortgage_progress/declineSelectionApproved",
    }),
    progress () {
      let total = this.mortgageProgresses.length

      return Math.round(this.mortgageProgresses.reduce((progress, item) => {
        if (item.received) {
          progress += (100 / total) / 3
        }
        if (item.sent) {
          progress += (100 / total) / 3
        }
        if (item.completed) {
          progress += (100 / total) / 3
        }

        return progress
      }, 0))
    },
    selectedDocumentTypes () {
      return this.documentTypes.filter(documentType => {
        return this.mortgageProgresses.filter(mortgageProgress => {
          return mortgageProgress.document_type_id === documentType.id
              || mortgageProgress.name.includes(documentType.name)
        }).length > 0
      })
    }
  },
  methods: {
    ...mapActions({
      setMortgage: "$_mortgage_progress/setMortgage",
      initMortgageProgress: "$_mortgage_progress/initMortgageProgress",
      updateMortgageProgressWithState: "$_mortgage_progress/updateMortgageProgressWithState",

      clearDeclineSelectionToSendToAgency: "$_mortgage_progress/clearDeclineSelectionToSendToAgency",
      clearDeclineSelectionToBeApproved: "$_mortgage_progress/clearDeclineSelectionToBeApproved",
      clearDeclineSelectionApproved: "$_mortgage_progress/clearDeclineSelectionApproved",
    }),
    initDocumentTypes () {
      return documentTypeApi.getAllWithHDN().then(response => {
        this.documentTypes = response.data
      })
    },
    onMortgageProgressUpdated (mortgageProgress) {
      this.updateMortgageProgressWithState(mortgageProgress)
    },
    onProgressGroupChange (mortgageProgressGroup) {
      this.mortgageProgressGroup = mortgageProgressGroup
    },
    onDocumentTypeSelect (documentType, documentTypeItemComponent) {
      mortgageProgressApi.create({
        mortgage_id: this.mortgage.id,
        name: documentType.name,
        document_type_id: documentType.id
      }).then(() => {
        documentTypeItemComponent.loading = false
        this.initMortgageProgress(this.mortgage.id)
      })
    },
    onPrintClick () {
      let printWindow = window.open(
          `/customer/${this.mortgage.customer_id}/mortgage/${this.mortgage.id}/progress/print`,
          'Printen',
          'height=800,width=850'
      )
      printWindow.window.print()
      printWindow.document.close()
    },
  }
}
</script>

<style scoped>
.mortgage-progress-list {
  margin-bottom: 15px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  line-height: 1.5;
}

.empty-mortgage-progress {
  margin: 15px 0 !important;
}

</style>
